import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { TrendingUp, Home, CheckCircle2, DollarSign, Repeat } from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-6">
      <Icon className="w-6 h-6 text-[#C49A45]" />
      <p className="font-metric text-4xl text-[#0A2E26] mt-3">{value}</p>
      <p className="text-sm text-[#5C5C5C] mt-1">{label}</p>
    </div>
  );
}

export default function Story() {
  const [story, setStory] = useState(null);

  useEffect(() => {
    api.get("/fund/story").then((r) => setStory(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />

      <section className="bg-[#0A2E26] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight max-w-2xl leading-tight"
          >
            Our track record,<br /><span className="text-[#C49A45]">in the open.</span>
          </motion.h1>
          <p className="text-white/70 mt-5 max-w-lg text-lg">
            Every cycle the fund completes — homes bought, rehabbed, sold, and the profit
            returned to members. Full transparency, always.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={DollarSign} label="Profit returned to members" value={story ? fmt(story.total_profit_distributed) : "$0"} />
          <Stat icon={Home} label="Homes sold" value={story ? story.total_homes_sold : 0} />
          <Stat icon={Repeat} label="Rentals retained" value={story ? story.total_rentals : 0} />
          <Stat icon={TrendingUp} label="Payouts distributed" value={story ? story.returns_count : 0} />
        </div>

        <h2 className="font-display font-bold text-3xl text-[#0A2E26] tracking-tight mt-16">Cycles</h2>
        <p className="text-[#5C5C5C] mt-2">Each cycle sells 10 homes and retains the 11th as a rental.</p>

        {!story || story.cycles.length === 0 ? (
          <div data-testid="empty-story" className="mt-8 bg-white border border-[#E5E5E5] rounded-xl p-12 text-center text-[#5C5C5C]">
            The first cycle is underway. Completed cycles will appear here.
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="cycles-list">
            {story.cycles.map((c, i) => (
              <motion.div
                key={c.cycle}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`cycle-${c.cycle}`}
                className="bg-white border border-[#E5E5E5] rounded-xl p-6 flex flex-wrap items-center gap-6"
              >
                <div className="w-14 h-14 rounded-xl bg-[#0A2E26] flex items-center justify-center shrink-0">
                  <span className="font-display font-bold text-xl text-[#C49A45]">#{c.cycle}</span>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-lg text-[#0A2E26]">Cycle {c.cycle}</h3>
                    {c.complete ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#059669] bg-[#E7F6EF] px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Complete
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-[#D97706] bg-[#FEF3E2] px-2 py-0.5 rounded-full">In progress</span>
                    )}
                  </div>
                  <p className="text-sm text-[#5C5C5C] mt-1">
                    {c.sold} of 10 sold · {c.rentals} rental{c.rentals === 1 ? "" : "s"} retained · {c.homes} home{c.homes === 1 ? "" : "s"} total
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-[#5C5C5C] font-semibold">Realized profit</p>
                  <p className="font-metric text-3xl text-[#059669]">{fmt(c.profit)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
